import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Hash, Building2, Users, Tag, Settings2, ChevronDown, ChevronRight,
  Play, Info, FileText, ArrowRight, X, GripVertical, Loader2,
  Wand2, PenLine, History, ArrowUp, AlertTriangle, Database, Filter,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import type {
  ExportJobRow,
  InvoiceRunSettings as IInvoiceRunSettings,
  InvoiceGroupingMode,
  InvoiceAssignment,
  InvoiceRunSummary,
} from './types';
import {
  INVOICE_GROUPING_MODE_INFO,
  INVOICE_GROUPING_FIELDS,
} from './types';
import { assignInvoiceNumbers } from './transforms';

// ─── Props ──────────────────────────────────────────────────────────

interface InvoiceRunSettingsProps {
  rows: ExportJobRow[];
  onInvoiceAssigned: (
    assignments: Map<string, InvoiceAssignment>,
    summary: InvoiceRunSummary,
    settings: IInvoiceRunSettings
  ) => void;
  onInvoiceCleared: () => void;
  currentAssignments: Map<string, InvoiceAssignment> | null;
  currentSummary: InvoiceRunSummary | null;
  /** Total number of matching rows across ALL pages (from Supabase count) */
  totalRowCount?: number;
  /** Whether an account scope filter is active on the template */
  hasAccountScope?: boolean;
  /** Number of accounts in the account scope */
  accountScopeCount?: number;
  /** Whether a per-column account filter is active */
  hasColumnAccountFilter?: boolean;
  /** Number of accounts in the per-column filter */
  columnAccountFilterCount?: number;
}

// ─── Last Run Info Type ─────────────────────────────────────────────

interface LastRunInfo {
  id: string;
  templateName: string;
  endingInvoiceNumber: number;
  startingInvoiceNumber: number;
  invoiceGroupCount: number;
  exportedAt: string;
  rowCount: number;
}

// ─── Grouping Mode Icons ────────────────────────────────────────────

const GROUPING_ICONS: Record<InvoiceGroupingMode, React.ReactNode> = {
  account_only: <Building2 className="w-3.5 h-3.5" />,
  account_plus_passenger: <Users className="w-3.5 h-3.5" />,
  account_plus_reference: <Tag className="w-3.5 h-3.5" />,
  custom_fields: <Settings2 className="w-3.5 h-3.5" />,
};

// ─── Component ──────────────────────────────────────────────────────

const InvoiceRunSettings: React.FC<InvoiceRunSettingsProps> = ({
  rows,
  onInvoiceAssigned,
  onInvoiceCleared,
  currentAssignments,
  currentSummary,
  totalRowCount,
  hasAccountScope,
  accountScopeCount,
  hasColumnAccountFilter,
  columnAccountFilterCount,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [startingNumber, setStartingNumber] = useState<number>(10001);
  const [groupingMode, setGroupingMode] = useState<InvoiceGroupingMode>('account_plus_passenger');
  const [customFields, setCustomFields] = useState<string[]>(['account_id', 'passenger_name']);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // Auto-suggest state
  const [invoiceMode, setInvoiceMode] = useState<'auto' | 'manual'>('auto');
  const [lastRunInfo, setLastRunInfo] = useState<LastRunInfo | null>(null);
  const [loadingLastRun, setLoadingLastRun] = useState(false);
  const [autoNumber, setAutoNumber] = useState<number | null>(null);

  const isAssigned = currentAssignments !== null && currentAssignments.size > 0;

  // ─── Pagination / Scope Warnings ────────────────────────────────
  const isPaginated = totalRowCount != null && totalRowCount > rows.length;
  const missingRows = isPaginated ? totalRowCount - rows.length : 0;

  // ─── Unique account IDs in current rows ─────────────────────────
  const uniqueAccountIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rows) {
      if (r.account_id) ids.add(r.account_id);
    }
    return ids;
  }, [rows]);

  // ─── Fetch Last Invoice Run ───────────────────────────────────
  const fetchLastInvoiceRun = useCallback(async () => {
    setLoadingLastRun(true);
    try {
      const { data, error } = await supabase
        .from('export_runs')
        .select('id, template_name_snapshot, ending_invoice_number, starting_invoice_number, invoice_group_count, exported_at, row_count')
        .not('ending_invoice_number', 'is', null)
        .order('exported_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows

      if (data && data.ending_invoice_number != null) {
        const info: LastRunInfo = {
          id: data.id,
          templateName: data.template_name_snapshot,
          endingInvoiceNumber: data.ending_invoice_number,
          startingInvoiceNumber: data.starting_invoice_number,
          invoiceGroupCount: data.invoice_group_count || 0,
          exportedAt: data.exported_at,
          rowCount: data.row_count,
        };
        setLastRunInfo(info);
        const nextNum = info.endingInvoiceNumber + 1;
        setAutoNumber(nextNum);
        // If in auto mode, set the starting number
        if (invoiceMode === 'auto') {
          setStartingNumber(nextNum);
        }
      } else {
        setLastRunInfo(null);
        setAutoNumber(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch last invoice run:', err);
      setLastRunInfo(null);
      setAutoNumber(null);
    } finally {
      setLoadingLastRun(false);
    }
  }, [invoiceMode]);

  // Fetch on mount
  useEffect(() => {
    fetchLastInvoiceRun();
  }, []);

  // When mode changes, update starting number
  useEffect(() => {
    if (invoiceMode === 'auto' && autoNumber != null) {
      setStartingNumber(autoNumber);
    }
  }, [invoiceMode, autoNumber]);

  // ─── Handle Assignment ──────────────────────────────────────────
  const handleAssign = useCallback(() => {
    if (rows.length === 0 || !startingNumber) return;

    const settings: IInvoiceRunSettings = {
      startingInvoiceNumber: startingNumber,
      groupingMode,
      customGroupFields: groupingMode === 'custom_fields' ? customFields : [],
    };

    const { assignments, summary } = assignInvoiceNumbers(rows, settings);
    onInvoiceAssigned(assignments, summary, settings);
  }, [rows, startingNumber, groupingMode, customFields, onInvoiceAssigned]);

  // ─── Custom Fields Management ───────────────────────────────────
  const toggleCustomField = (field: string) => {
    setCustomFields(prev => {
      if (prev.includes(field)) return prev.filter(f => f !== field);
      if (prev.length >= 4) return prev;
      return [...prev, field];
    });
  };

  const moveCustomField = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= customFields.length) return;
    const newFields = [...customFields];
    const [moved] = newFields.splice(fromIdx, 1);
    newFields.splice(toIdx, 0, moved);
    setCustomFields(newFields);
  };

  // ─── Grouping Description ───────────────────────────────────────
  const groupingDescription = useMemo(() => {
    const info = INVOICE_GROUPING_MODE_INFO[groupingMode];
    if (groupingMode === 'custom_fields' && customFields.length > 0) {
      const labels = customFields.map(f =>
        INVOICE_GROUPING_FIELDS.find(gf => gf.field === f)?.label || f
      );
      return `Group by: ${labels.join(' + ')}`;
    }
    return info.description;
  }, [groupingMode, customFields]);

  // ─── Format relative time ───────────────────────────────────────
  const formatRelativeTime = (dateStr: string): string => {
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(d);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 pt-3 px-4 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-indigo-400" />
              <CardTitle className="text-sm font-semibold">Invoice Run Settings</CardTitle>
              {isAssigned && currentSummary && (
                <Badge className="bg-indigo-900/40 text-indigo-400 border-indigo-700/50 text-[10px] px-1.5 py-0">
                  {currentSummary.uniqueGroups} invoices assigned
                </Badge>
              )}
              {isPaginated && !isAssigned && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/50 text-[10px] px-1.5 py-0 gap-0.5">
                      <AlertTriangle className="w-2.5 h-2.5" />
                      Partial
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="bg-card border-border text-xs max-w-[260px]">
                    <p className="text-amber-400 font-medium">Pagination Warning</p>
                    <p className="text-muted-foreground mt-0.5">Only {rows.length} of {totalRowCount} matching rows are loaded. Invoice numbers will only be assigned to the current page.</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {collapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        </CardHeader>

        {!collapsed && (
          <CardContent className="px-4 pb-3 pt-0 space-y-3">
            {/* Info Banner */}
            <div className="flex items-start gap-1.5 text-[9px] text-muted-foreground/70 bg-secondary/30 border border-border/40 rounded px-2 py-1.5">
              <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>
                Assign sequential invoice numbers to the filtered dataset. Rows sharing the same grouping key receive the same invoice number.
              </span>
            </div>

            {/* ═══ PAGINATION WARNING ═══ */}
            {isPaginated && (
              <div className="flex items-start gap-2 bg-amber-900/15 border border-amber-700/40 rounded px-2.5 py-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-semibold text-amber-400">
                    Partial Data — {missingRows.toLocaleString()} rows not loaded
                  </p>
                  <p className="text-[9px] text-amber-400/70 mt-0.5">
                    Only <strong>{rows.length}</strong> of <strong>{totalRowCount?.toLocaleString()}</strong> matching rows are loaded (page limit: 100).
                    Invoice numbers will only be assigned to the <strong>{rows.length}</strong> rows on this page.
                    The remaining <strong>{missingRows.toLocaleString()}</strong> rows on other pages will NOT receive invoice numbers and will NOT be exported.
                  </p>
                  <p className="text-[9px] text-amber-400/60 mt-1">
                    To include all rows: narrow your date range or account filters so the total fits within one page, or export in batches per page.
                  </p>
                </div>
              </div>
            )}

            {/* ═══ ACCOUNT SCOPE WARNING ═══ */}
            {hasAccountScope && (
              <div className="flex items-start gap-2 bg-blue-900/10 border border-blue-700/30 rounded px-2.5 py-1.5">
                <Filter className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] text-blue-400 font-medium">
                    Template Account Scope Active — {accountScopeCount || '?'} accounts selected
                  </p>
                  <p className="text-[9px] text-blue-400/60 mt-0.5">
                    Only rows matching the template's account scope will receive invoice numbers.
                    Accounts not in the scope are excluded from this view and the export.
                  </p>
                </div>
              </div>
            )}

            {/* ═══ PER-COLUMN ACCOUNT FILTER WARNING ═══ */}
            {hasColumnAccountFilter && (
              <div className="flex items-start gap-2 bg-teal-900/10 border border-teal-700/30 rounded px-2.5 py-1.5">
                <Filter className="w-3 h-3 text-teal-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] text-teal-400 font-medium">
                    Column Account Filter Active — {columnAccountFilterCount || '?'} accounts
                  </p>
                  <p className="text-[9px] text-teal-400/60 mt-0.5">
                    The Account ID column has a per-column filter. Invoice numbers will be assigned to all {rows.length} rows here,
                    but the export will only include the {columnAccountFilterCount || '?'} filtered accounts.
                  </p>
                </div>
              </div>
            )}

            {/* Invoice Number Mode Toggle */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Invoice Number</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                <button
                  className={`flex items-center gap-1.5 px-2 py-2 rounded text-[10px] border transition-colors text-left ${
                    invoiceMode === 'auto'
                      ? 'bg-indigo-900/30 text-indigo-400 border-indigo-700/50'
                      : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-border'
                  }`}
                  onClick={() => setInvoiceMode('auto')}
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  <span className="font-medium">Auto (Next)</span>
                </button>
                <button
                  className={`flex items-center gap-1.5 px-2 py-2 rounded text-[10px] border transition-colors text-left ${
                    invoiceMode === 'manual'
                      ? 'bg-indigo-900/30 text-indigo-400 border-indigo-700/50'
                      : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-border'
                  }`}
                  onClick={() => setInvoiceMode('manual')}
                >
                  <PenLine className="w-3.5 h-3.5" />
                  <span className="font-medium">Manual</span>
                </button>
              </div>
            </div>

            {/* Last Run Info (shown in auto mode) */}
            {invoiceMode === 'auto' && (
              <div className="border border-border/40 rounded bg-secondary/20 p-2">
                {loadingLastRun ? (
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Looking up last invoice run...
                  </div>
                ) : lastRunInfo ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <History className="w-3 h-3 text-indigo-400" />
                      <span className="text-[10px] font-semibold text-indigo-400 uppercase">Last Invoice Run</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Template:</span>
                        <span className="text-foreground font-medium truncate ml-1">{lastRunInfo.templateName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">When:</span>
                        <span className="text-foreground">{formatRelativeTime(lastRunInfo.exportedAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Range:</span>
                        <span className="text-indigo-400 font-medium">{lastRunInfo.startingInvoiceNumber} - {lastRunInfo.endingInvoiceNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invoices:</span>
                        <span className="text-foreground">{lastRunInfo.invoiceGroupCount}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 pt-1 border-t border-border/30 mt-1">
                      <ArrowUp className="w-3 h-3 text-green-400" />
                      <span className="text-[10px] text-green-400 font-semibold">
                        Next in sequence: {autoNumber}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <Info className="w-3 h-3" />
                    <span>No previous invoice runs found. Starting from default.</span>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchLastInvoiceRun}
                  disabled={loadingLastRun}
                  className="h-5 text-[9px] text-muted-foreground hover:text-foreground mt-1 px-1"
                >
                  <History className={`w-2.5 h-2.5 mr-0.5 ${loadingLastRun ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            )}

            {/* Starting Invoice Number */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">
                Starting Invoice Number
                {invoiceMode === 'auto' && autoNumber != null && (
                  <span className="text-indigo-400 ml-1">(auto-filled)</span>
                )}
              </Label>
              <Input
                type="number"
                value={startingNumber}
                onChange={e => {
                  setStartingNumber(parseInt(e.target.value) || 0);
                  // If user manually edits in auto mode, switch to manual
                  if (invoiceMode === 'auto') {
                    const newVal = parseInt(e.target.value) || 0;
                    if (autoNumber != null && newVal !== autoNumber) {
                      setInvoiceMode('manual');
                    }
                  }
                }}
                placeholder="e.g. 10001"
                className="dark-input text-xs h-8"
                min={1}
              />
            </div>

            {/* Grouping Mode */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Invoice Grouping Mode</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {(Object.keys(INVOICE_GROUPING_MODE_INFO) as InvoiceGroupingMode[]).map(mode => {
                  const info = INVOICE_GROUPING_MODE_INFO[mode];
                  const isActive = groupingMode === mode;
                  return (
                    <Tooltip key={mode}>
                      <TooltipTrigger asChild>
                        <button
                          className={`flex items-center gap-1.5 px-2 py-2 rounded text-[10px] border transition-colors text-left ${
                            isActive
                              ? 'bg-indigo-900/30 text-indigo-400 border-indigo-700/50'
                              : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-border'
                          }`}
                          onClick={() => setGroupingMode(mode)}
                        >
                          {GROUPING_ICONS[mode]}
                          <span className="font-medium">{info.label}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-card border-border text-xs max-w-[220px]">
                        {info.description}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
              <p className="text-[9px] text-muted-foreground/60 mt-1">{groupingDescription}</p>
            </div>

            {/* Custom Fields Builder */}
            {groupingMode === 'custom_fields' && (
              <div className="border border-border/40 rounded p-2 space-y-2">
                <div className="flex items-center gap-1.5">
                  <Settings2 className="w-3 h-3 text-indigo-400" />
                  <Label className="text-[10px] text-muted-foreground uppercase">Custom Grouping Fields (1-4)</Label>
                </div>

                {/* Selected fields in order */}
                {customFields.length > 0 && (
                  <div className="space-y-1">
                    {customFields.map((field, idx) => {
                      const label = INVOICE_GROUPING_FIELDS.find(f => f.field === field)?.label || field;
                      return (
                        <div key={field} className="flex items-center gap-1.5 bg-indigo-900/10 border border-indigo-700/20 rounded px-2 py-1">
                          <span className="text-[9px] text-muted-foreground/50 w-3">{idx + 1}</span>
                          <span className="text-[10px] text-indigo-400 flex-1">{label}</span>
                          <button
                            className="text-muted-foreground/40 hover:text-muted-foreground disabled:opacity-20"
                            disabled={idx === 0}
                            onClick={() => moveCustomField(idx, idx - 1)}
                            title="Move up"
                          >
                            <GripVertical className="w-3 h-3" />
                          </button>
                          <button
                            className="text-muted-foreground/40 hover:text-red-400"
                            onClick={() => toggleCustomField(field)}
                            title="Remove"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Available fields */}
                <div className="flex flex-wrap gap-1">
                  {INVOICE_GROUPING_FIELDS.map(f => {
                    const isSelected = customFields.includes(f.field);
                    return (
                      <button
                        key={f.field}
                        className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${
                          isSelected
                            ? 'bg-indigo-900/30 text-indigo-400 border-indigo-700/50'
                            : customFields.length >= 4
                              ? 'bg-secondary/20 text-muted-foreground/30 border-border/20 cursor-not-allowed'
                              : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-border hover:text-foreground'
                        }`}
                        onClick={() => toggleCustomField(f.field)}
                        disabled={!isSelected && customFields.length >= 4}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Assign / Clear Buttons */}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={rows.length === 0 || !startingNumber}
                className="flex-1 h-8 text-xs bg-indigo-700 hover:bg-indigo-600 text-white"
              >
                <Play className="w-3 h-3 mr-1" />
                {isAssigned ? 'Re-assign Invoice Numbers' : 'Assign Invoice Numbers'}
                {rows.length > 0 && (
                  <span className="ml-1 opacity-70">({rows.length} rows)</span>
                )}
              </Button>
              {isAssigned && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onInvoiceCleared}
                  className="h-8 text-xs border-border text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* ═══ POST-ASSIGNMENT: Data Coverage Check ═══ */}
            {isAssigned && currentSummary && isPaginated && (
              <div className="flex items-start gap-2 bg-amber-900/15 border border-amber-700/40 rounded px-2.5 py-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-semibold text-amber-400">
                    Invoice numbers assigned to {currentSummary.totalRows} of {totalRowCount?.toLocaleString()} total rows
                  </p>
                  <p className="text-[9px] text-amber-400/70 mt-0.5">
                    {missingRows.toLocaleString()} rows on other pages did NOT receive invoice numbers.
                    The export will only include the {currentSummary.totalRows} rows on this page.
                    {uniqueAccountIds.size > 0 && (
                      <> Covering {uniqueAccountIds.size} unique account{uniqueAccountIds.size !== 1 ? 's' : ''}.</>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Invoice Run Summary */}
            {isAssigned && currentSummary && (
              <div className="border border-indigo-700/30 rounded bg-indigo-900/10 p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <FileText className="w-3 h-3 text-indigo-400" />
                    <span className="text-[10px] font-semibold text-indigo-400 uppercase">Invoice Run Summary</span>
                  </div>
                  <button
                    className="text-[9px] text-muted-foreground hover:text-foreground"
                    onClick={() => setSummaryExpanded(!summaryExpanded)}
                  >
                    {summaryExpanded ? 'Collapse' : 'Expand'}
                  </button>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Starting #:</span>
                    <span className="text-foreground font-medium">{currentSummary.startingNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ending #:</span>
                    <span className="text-foreground font-medium">{currentSummary.endingNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Invoices:</span>
                    <span className="text-indigo-400 font-semibold">{currentSummary.uniqueGroups}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Rows:</span>
                    <span className="text-foreground font-medium">{currentSummary.totalRows}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Accounts:</span>
                    <span className="text-foreground font-medium">{uniqueAccountIds.size}</span>
                  </div>
                  {isPaginated && (
                    <div className="flex justify-between">
                      <span className="text-amber-400">Not loaded:</span>
                      <span className="text-amber-400 font-medium">{missingRows.toLocaleString()} rows</span>
                    </div>
                  )}
                </div>

                {/* Group Breakdown (expanded) */}
                {summaryExpanded && currentSummary.groupBreakdown.length > 0 && (
                  <div className="mt-1.5 border-t border-indigo-700/20 pt-1.5">
                    <p className="text-[9px] text-muted-foreground/60 uppercase mb-1">Invoice Breakdown</p>
                    <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                      {currentSummary.groupBreakdown.map((group, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-[10px] px-1.5 py-1 rounded bg-secondary/20 border border-border/20"
                        >
                          <Badge className="bg-indigo-900/40 text-indigo-400 border-indigo-700/50 text-[9px] px-1.5 py-0 flex-shrink-0 font-mono">
                            #{group.invoiceNumber}
                          </Badge>
                          <span className="text-muted-foreground flex-shrink-0">{group.accountId || '-'}</span>
                          {group.passengerName && (
                            <>
                              <ArrowRight className="w-2.5 h-2.5 text-muted-foreground/40 flex-shrink-0" />
                              <span className="text-foreground truncate">{group.passengerName}</span>
                            </>
                          )}
                          <span className="ml-auto text-muted-foreground/60 flex-shrink-0">
                            {group.rowCount} row{group.rowCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </TooltipProvider>
  );
};

export default InvoiceRunSettings;
